/*
 * Copyright (c) 2023 Bluemate B.V.
 */

import {ErrorType, ResponseData} from '@dvrd/dvr-controls';
import React, {ForwardedRef, forwardRef, useImperativeHandle, useState} from 'react';
import {setVatNumber as sendRequest} from '../../../../util/requestSender';
import SetVatNumber from './setVatNumber';
import {ActivateHandle} from '../../../../util/interfaces';
import Subscription from '../../../../models/subscriptionModel';
import {showSnackbar} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

interface Props {
    onSuccess: (subscription: Subscription) => void;
}

function SetVatNumberController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const {t} = useTranslation();
    const {onSuccess} = props;
    const [vatNumber, setVatNumber] = useState('');
    const [active, setActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<ErrorType>(null);

    function onActivate() {
        setActive(true);
    }

    function onClose() {
        setActive(false);
        setVatNumber('');
    }

    function onChange(value: string) {
        setVatNumber(value);
        if (error) setError(null);
    }

    function onFocus() {
        if (error) setError(null);
    }

    function onSubmit() {
        if (!isValid()) return;
        setLoading(true);
        sendRequest({
            data: {vat_number: vatNumber},
            callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    showSnackbar(t('settings.subscription.vatNumberSaved', 'Het BTW-nummer is opgeslagen'));
                    onClose();
                    onSuccess(new Subscription(data.subscription));
                } else
                    setError(
                        data.message ??
                            t(
                                'settings.subscription.vatNumberFailed',
                                'Het instellen van het BTW-nummer is niet gelukt'
                            )
                    );
            }
        });
    }

    function isValid(): boolean {
        if (!vatNumber) {
            setError(t('settings.subscription.vatNumberRequired', 'Vul het BTW-nummer in'));
            return false;
        }
        return true;
    }

    useImperativeHandle(ref, () => ({
        activate: onActivate
    }));

    return (
        <SetVatNumber
            onChange={onChange}
            onFocus={onFocus}
            onSubmit={onSubmit}
            onClose={onClose}
            vatNumber={vatNumber}
            active={active}
            loading={loading}
            error={error}
        />
    );
}

export default forwardRef<ActivateHandle, Props>(SetVatNumberController);
